import { buildPowerLineSafetyHtml } from '../utils/power-line-safety';
import { buildAirportCorridorHtml } from '../utils/airport-corridor';
import { bdot10kLayerLabel, safeHref } from '../utils/formatters';

// Most BDOT10k features predate the power-line safety overlay and therefore do
// not carry this property at all. Treat a missing flag as an ordinary polygon.
export function createOrdinaryOtoczeniePolygonFilter() {
    return [
        'all',
        ['==', ['geometry-type'], 'Polygon'],
        ['any',
            ['!', ['has', 'power_line_safety_buffer']],
            ['!=', ['get', 'power_line_safety_buffer'], true]
        ],
        // Corridor strips get their own layers below; without this they would also be
        // drawn as ordinary objects and clutter the list.
        ['any',
            ['!', ['has', 'airport_corridor_zone']],
            ['!=', ['get', 'airport_corridor_zone'], true]
        ]
    ];
}

export function createOtoczenieModule(config) {
    var bdot10kImpactBalanceLabel = config.bdot10kImpactBalanceLabel;
    var bdot10kImpactSummary = config.bdot10kImpactSummary;
    var bdot10kLabel = config.bdot10kLabel;
    var bdot10kTranslate = config.bdot10kTranslate;
    var renderBdot10kFunctions = config.renderBdot10kFunctions;
    var bringDataLayersToFront = config.bringDataLayersToFront;
    var consumeHandledMapClick = config.consumeHandledMapClick;
    var esc = config.esc;
    var getController = config.getController;
    var getFeatureCentroid = config.getFeatureCentroid;
    var getLayer = config.getLayer;
    var getMap = config.getMap;
    var setLayer = config.setLayer;

    var popup = null;
    var interactionsBound = false;
    var layerIds = [
        'lyr-otoczenie-airport-corridor-fill',
        'lyr-otoczenie-airport-corridor-line',
        'lyr-otoczenie-power-line-buffer-fill',
        'lyr-otoczenie-power-line-buffer-line',
        'lyr-otoczenie-fill',
        'lyr-otoczenie-line',
        'lyr-otoczenie-linestring',
        'lyr-otoczenie-circle'
    ];
    var clickLayerIds = layerIds.slice();

    function toggleLayer() {
        var controller = getController ? getController() : null;
        if (controller) {
            controller.toggle();
        }
    }

    function buildFeatureInfoHtml(feature) {
        var props = feature && feature.properties ? feature.properties : {};
        var label = bdot10kLabel ? bdot10kLabel(props) : (props.label || props.nazwa || props.object_label || 'Obiekt Bazy obiektów topograficznych');
        var impactSummary = bdot10kImpactSummary ? bdot10kImpactSummary(props) : '';
        var balanceLabel = bdot10kImpactBalanceLabel ? bdot10kImpactBalanceLabel(props) : '';
        var lines = [];

        if (props.airport_corridor_zone) {
            return buildAirportCorridorHtml(props, esc);
        }

        if (props.power_line_safety_buffer) {
            return buildPowerLineSafetyHtml(props, label, esc);
        }

        lines.push('<div class="nm-popup-card">');
        lines.push('<div class="nm-popup-kicker">Otoczenie</div>');
        lines.push('<div class="nm-popup-title">' + esc(label) + '</div>');
        if (impactSummary) {
            lines.push('<div class="nm-popup-summary">' + esc(impactSummary) + '</div>');
        }
        if (props.category_label) {
            lines.push('<div class="nm-popup-meta"><b>Kategoria:</b> ' + esc(props.category_label) + '</div>');
        }
        var layerLabel = bdot10kLayerLabel(props);
        if (layerLabel) {
            lines.push('<div class="nm-popup-meta"><b>Warstwa:</b> ' + esc(layerLabel) + '</div>');
        }
        if (props.object_label && props.object_label !== label) {
            lines.push('<div class="nm-popup-meta"><b>Typ:</b> ' + esc(props.object_label) + '</div>');
        }
        if (props.nazwa && props.nazwa !== label) {
            lines.push('<div class="nm-popup-meta"><b>Nazwa:</b> ' + esc(props.nazwa) + '</div>');
        }
        var detailedFunctions = renderBdot10kFunctions(props, esc);
        if (detailedFunctions) {
            lines.push('<div class="nm-popup-meta"><b>Funkcja szczegółowa:</b> ' + detailedFunctions + '</div>');
        }
        var rodzajLabel = props.rodzaj_label || (props.rodzaj ? bdot10kTranslate(props.rodzaj) : '');
        if (rodzajLabel) {
            lines.push('<div class="nm-popup-meta"><b>Rodzaj:</b> ' + esc(rodzajLabel) + '</div>');
        }
        if (balanceLabel) {
            lines.push('<div class="nm-popup-meta"><b>Ocena:</b> ' + esc(balanceLabel) + '</div>');
        }
        var detailUrl = safeHref(props.detail_url);
        if (detailUrl) {
            lines.push('<a class="nm-popup-link" href="' + esc(detailUrl) + '">Pełne szczegóły obiektu</a>');
        }
        lines.push('</div>');

        return lines.join('');
    }

    function showFeatureInfo(feature, latlng) {
        var map = getMap ? getMap() : null;
        var anchor = latlng || (getFeatureCentroid ? getFeatureCentroid(feature) : null);
        var html = buildFeatureInfoHtml(feature);

        if (!map || !anchor || !html) {
            return;
        }

        if (!popup) {
            popup = new maplibregl.Popup({ maxWidth: '280px', closeButton: true });
        }

        if (typeof popup.setMaxWidth === 'function') {
            popup.setMaxWidth(feature && feature.properties && feature.properties.power_line_safety_buffer ? '380px' : '280px');
        }

        popup.setLngLat([anchor.lng, anchor.lat]).setHTML(html).addTo(map);
    }

    function ensureInteractions(map) {
        if (interactionsBound) return;
        interactionsBound = true;

        clickLayerIds.forEach(function(layerId) {
            config.registerCursorHandlers(map, layerId);
        });

        map.on('click', 'lyr-otoczenie-power-line-buffer-fill', function(event) {
            // The map's point-query handler includes these buffers together
            // with all other objects and respects measurement/submission modes.
            if (config.usePointQueryPanel) return;
            if (!event.features || !event.features.length) return;
            consumeHandledMapClick(event);
            event.preventDefault();
            if (event.originalEvent && typeof event.originalEvent.stopPropagation === 'function') {
                event.originalEvent.stopPropagation();
            }
            showFeatureInfo(event.features[0], event.lngLat);
        });
    }

    function renderData(data) {
        var map = getMap ? getMap() : null;
        if (!map) return null;

        if (getLayer()) {
            config.removeSourceAndLayers(map, 'src-otoczenie', layerIds);
            setLayer(null);
        }

        if (!data || !data.features || !data.features.length) {
            return null;
        }

        config.addOrUpdateGeoJsonSource(map, 'src-otoczenie', data);

        map.addLayer({
            id: 'lyr-otoczenie-airport-corridor-fill', type: 'fill', source: 'src-otoczenie',
            filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'airport_corridor_zone'], true]],
            paint: {
                // Lower opacity than the power-line buffer: the corridor covers tens of
                // square kilometres, so anything stronger swamps the map underneath.
                'fill-color': '#7c3aed',
                'fill-opacity': 0.10
            }
        });
        map.addLayer({
            id: 'lyr-otoczenie-airport-corridor-line', type: 'line', source: 'src-otoczenie',
            filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'airport_corridor_zone'], true]],
            paint: {
                'line-color': '#5b21b6',
                'line-width': 1.5,
                'line-dasharray': [4, 3],
                'line-opacity': 0.75
            }
        });

        map.addLayer({
            id: 'lyr-otoczenie-power-line-buffer-fill', type: 'fill', source: 'src-otoczenie',
            filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'power_line_safety_buffer'], true]],
            paint: {
                'fill-color': '#f59e0b',
                'fill-opacity': 0.18
            }
        });
        map.addLayer({
            id: 'lyr-otoczenie-power-line-buffer-line', type: 'line', source: 'src-otoczenie',
            filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'power_line_safety_buffer'], true]],
            paint: {
                'line-color': '#b45309',
                'line-width': 1.5,
                'line-dasharray': [3, 2],
                'line-opacity': 0.9
            }
        });

        map.addLayer({
            id: 'lyr-otoczenie-fill', type: 'fill', source: 'src-otoczenie',
            filter: createOrdinaryOtoczeniePolygonFilter(),
            paint: {
                'fill-color': ['case',
                    ['==', ['get', 'negative'], true], 'rgba(220,38,38,0.15)',
                    ['==', ['get', 'negative'], false], 'rgba(21,128,61,0.15)',
                    'rgba(15,118,110,0.15)'
                ],
                'fill-opacity': 1
            }
        });
        map.addLayer({
            id: 'lyr-otoczenie-line', type: 'line', source: 'src-otoczenie',
            filter: createOrdinaryOtoczeniePolygonFilter(),
            paint: {
                'line-color': ['case',
                    ['==', ['get', 'negative'], true], '#dc2626',
                    ['==', ['get', 'negative'], false], '#15803d',
                    '#0f766e'
                ],
                'line-width': 1.5
            }
        });
        map.addLayer({
            id: 'lyr-otoczenie-linestring', type: 'line', source: 'src-otoczenie',
            filter: ['==', '$type', 'LineString'],
            paint: {
                'line-color': ['case',
                    ['==', ['get', 'negative'], true], '#dc2626',
                    ['==', ['get', 'negative'], false], '#15803d',
                    '#0f766e'
                ],
                'line-width': 4,
                'line-opacity': 0.85
            }
        });
        map.addLayer({
            id: 'lyr-otoczenie-circle', type: 'circle', source: 'src-otoczenie',
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-radius': 6,
                'circle-color': ['case',
                    ['==', ['get', 'negative'], true], '#dc2626',
                    ['==', ['get', 'negative'], false], '#15803d',
                    '#0f766e'
                ],
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 1,
                'circle-opacity': 0.88
            }
        });

        setLayer('src-otoczenie');
        ensureInteractions(map);
        bringDataLayersToFront();
        return getLayer();
    }

    function load() {
        var controller = getController ? getController() : null;
        if (!controller) return;
        controller.load();
    }

    function syncToggleVisibility() {
        var controller = getController ? getController() : null;
        if (controller) {
            controller.syncVisibility();
        }
    }

    return {
        load: load,
        renderData: renderData,
        showFeatureInfo: showFeatureInfo,
        syncToggleVisibility: syncToggleVisibility,
        toggleLayer: toggleLayer,
    };
}
